import {
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  Button,
} from '@dev-spendesk/grapes';
import { useState, type ReactNode } from 'react';

import { useTranslation } from 'src/core/common/hooks/useTranslation';

import { LearnHowToChangeIllustration } from './LearnHowToChangeIllustration';
import { Step } from './Step';
import { OrderedStep, OrderedSteps } from '../../../components/OrderedSteps';

export const LearnHowToChangeSettings = ({
  actions,
  exportLevel,
}: {
  actions: ReactNode;
  exportLevel: 'header' | 'headerLine';
}) => {
  const { t } = useTranslation('global');
  const [selectTab, setSelectTab] = useState<'current' | 'recommended'>(
    'current',
  );

  const customSettingsNetsuiteGuides =
    exportLevel === 'header'
      ? [
          t(
            'accounting-integration.exportSettingsOnboardingGuide.learnToChangeSettings.standard.h1l1',
          ),
          t(
            'accounting-integration.exportSettingsOnboardingGuide.learnToChangeSettings.standard.h1l2',
          ),
          t(
            'accounting-integration.exportSettingsOnboardingGuide.learnToChangeSettings.custom.h1l3',
          ),
          t(
            'accounting-integration.exportSettingsOnboardingGuide.learnToChangeSettings.custom.h1l4',
          ),
          t(
            'accounting-integration.exportSettingsOnboardingGuide.learnToChangeSettings.custom.h1l5',
          ),
          t(
            'accounting-integration.exportSettingsOnboardingGuide.learnToChangeSettings.custom.h1l6',
          ),
        ]
      : [
          t(
            'accounting-integration.exportSettingsOnboardingGuide.learnToChangeSettings.standard.h1l1',
          ),
          t(
            'accounting-integration.exportSettingsOnboardingGuide.learnToChangeSettings.custom.h1l4',
          ),
          t(
            'accounting-integration.exportSettingsOnboardingGuide.learnToChangeSettings.custom.h1l5',
          ),
        ];

  return (
    <Step
      illustration={
        <LearnHowToChangeIllustration
          selectedTab={selectTab}
          exportLevel={exportLevel}
        />
      }
      actions={actions}
    >
      <h1 className="heading-m">
        {t(
          'accounting-integration.exportSettingsOnboardingGuide.learnToChangeSettings.title',
        )}
      </h1>

      <Tabs
        onChange={(index) =>
          setSelectTab(index === 0 ? 'current' : 'recommended')
        }
      >
        <TabList className="mt-32 mb-16">
          <Tab>
            {t(
              'accounting-integration.exportSettingsOnboardingGuide.learnToChangeSettings.standard.tab',
            )}
          </Tab>
          <Tab>
            {t(
              'accounting-integration.exportSettingsOnboardingGuide.learnToChangeSettings.custom.tab',
            )}
          </Tab>
        </TabList>
        <TabPanels>
          <TabPanel>
            <OrderedSteps className="my-16">
              <OrderedStep
                title={t(
                  'accounting-integration.exportSettingsOnboardingGuide.learnToChangeSettings.standard.h1',
                )}
              >
                <ol className="my-8 list-decimal pl-24">
                  <li className="mb-8">
                    {t(
                      'accounting-integration.exportSettingsOnboardingGuide.learnToChangeSettings.standard.h1l1',
                    )}
                  </li>
                  <li className="mb-8">
                    {t(
                      'accounting-integration.exportSettingsOnboardingGuide.learnToChangeSettings.standard.h1l2',
                    )}
                  </li>
                </ol>
              </OrderedStep>
              <OrderedStep
                title={t(
                  'accounting-integration.exportSettingsOnboardingGuide.learnToChangeSettings.standard.h2',
                )}
              >
                <ol className="my-8 list-decimal pl-24">
                  <li className="mb-8">
                    {t(
                      'accounting-integration.exportSettingsOnboardingGuide.learnToChangeSettings.standard.h2l1',
                    )}
                  </li>
                  <li className="mb-8">
                    {t(
                      'accounting-integration.exportSettingsOnboardingGuide.learnToChangeSettings.standard.h2l2',
                    )}
                  </li>
                </ol>
              </OrderedStep>
            </OrderedSteps>
          </TabPanel>
          <TabPanel>
            <OrderedSteps className="my-16">
              <OrderedStep
                title={t(
                  'accounting-integration.exportSettingsOnboardingGuide.learnToChangeSettings.standard.h1',
                )}
              >
                <ol className="my-8 list-decimal pl-24">
                  {customSettingsNetsuiteGuides.map((line) => (
                    <li className="mb-8" key={line}>
                      {line}
                    </li>
                  ))}
                </ol>
              </OrderedStep>
              <OrderedStep
                title={t(
                  'accounting-integration.exportSettingsOnboardingGuide.learnToChangeSettings.standard.h2',
                )}
              >
                <ol className="my-8 list-decimal pl-24">
                  <li className="mb-8">
                    {t(
                      'accounting-integration.exportSettingsOnboardingGuide.learnToChangeSettings.standard.h2l1',
                    )}
                  </li>
                  <li className="mb-8">
                    {t(
                      'accounting-integration.exportSettingsOnboardingGuide.learnToChangeSettings.standard.h2l2',
                    )}
                  </li>
                </ol>
              </OrderedStep>
            </OrderedSteps>
          </TabPanel>
        </TabPanels>
      </Tabs>

      <Button
        variant="secondaryNeutral"
        component="a"
        href="https://spendesk.com/"
        target="_blank"
        rel="noopener"
        iconName="arrow-top-right-square"
        iconPosition="right"
        text={t('misc.helpCenterArticle')}
      />
    </Step>
  );
};
